import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faExclamationTriangle,
  faDownload,
  faFileCsv,
  faFileExcel,
  faEye,
  faSpinner,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const DEFAULT_MAX_PRN_PER_SEARCH = 500;

const parseMaxPrnPerSearch = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_PRN_PER_SEARCH;
};

const FindDataUsingPrnPage = () => {
  const [prnInput, setPrnInput] = useState("");
  const [providerNameFilter, setProviderNameFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [resultsSearchTerm, setResultsSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "payment_ref_no", direction: "asc" });
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState(null);
  const [maxPrnPerSearch, setMaxPrnPerSearch] = useState(DEFAULT_MAX_PRN_PER_SEARCH);
  const [prnSearchLimitError, setPrnSearchLimitError] = useState("");

  const normalizePrnInputToCsv = (rawInput) => {
    return rawInput
      .split(/[\r\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .join(",");
  };

  const countNormalizedPrns = (normalizedPrnList) => {
    if (!normalizedPrnList) return 0;

    return normalizedPrnList
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean).length;
  };

  const loadPrnSearchSettings = useCallback(async () => {
    const token = localStorage.getItem("authentication/token");
    const tokenType = localStorage.getItem("authentication/token_type") || "Bearer";

    if (!token) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    const response = await fetch(`${BASE_URL}/provider-payment/dpr/search-prn-settings`, {
      method: "GET",
      headers: {
        "Authorization": `${tokenType} ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok || data.status !== "success") {
      throw new Error(data.message || "Failed to load PRN search settings.");
    }

    return {
      maxPrnPerSearch: parseMaxPrnPerSearch(data.data?.max_payment_ref_no_per_search),
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializePrnSearchSettings = async () => {
      try {
        const settings = await loadPrnSearchSettings();
        if (!isMounted) return;

        setMaxPrnPerSearch(settings.maxPrnPerSearch);
        setPrnSearchLimitError("");
      } catch (err) {
        if (!isMounted) return;

        setPrnSearchLimitError(
          `Unable to load PRN search limit. Using default maximum ${DEFAULT_MAX_PRN_PER_SEARCH}.`
        );
      }
    };

    initializePrnSearchSettings();

    return () => {
      isMounted = false;
    };
  }, [loadPrnSearchSettings]);

  const getSortableValue = (record, key) => {
    const value = record?.[key];
    if (value == null) return "";
    return String(value).toLowerCase();
  };

  const filteredAndSortedResults = useMemo(() => {
    const normalizedSearch = resultsSearchTerm.trim().toLowerCase();

    const filtered = !normalizedSearch
      ? [...results]
      : results.filter((record) => {
          const values = [
            record.payment_ref_no,
            record.provider_name,
            record.pis_file,
            record.pis_file_v2,
            record.deposit_slip_file,
            record.deposit_slip_file_v2,
            record.form_2307_file,
            record.form_2307_file_v2,
          ];

          return values.some((value) =>
            String(value ?? "").toLowerCase().includes(normalizedSearch)
          );
        });

    const sorted = [...filtered].sort((a, b) => {
      const aVal = getSortableValue(a, sortConfig.key);
      const bVal = getSortableValue(b, sortConfig.key);
      const comparison = aVal.localeCompare(bVal, undefined, {
        numeric: true,
        sensitivity: "base",
      });

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [results, resultsSearchTerm, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "-";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const handleSearch = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    
    const normalizedPrnList = normalizePrnInputToCsv(prnInput);
    const normalizedPrnCount = countNormalizedPrns(normalizedPrnList);

    // Validate input
    if (!normalizedPrnList) {
      Swal.fire({
        title: "Missing Input",
        text: "Please enter at least one Payment Reference Number (PRN)",
        icon: "warning",
        confirmButtonColor: "#0ea5e9",
      });
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      let activeMaxPrnPerSearch = maxPrnPerSearch;

      try {
        const settings = await loadPrnSearchSettings();
        activeMaxPrnPerSearch = settings.maxPrnPerSearch;
        setMaxPrnPerSearch(settings.maxPrnPerSearch);
        setPrnSearchLimitError("");
      } catch (settingsError) {
        setPrnSearchLimitError(
          `Unable to refresh PRN search limit. Using current maximum ${activeMaxPrnPerSearch}.`
        );
      }

      if (normalizedPrnCount > activeMaxPrnPerSearch) {
        const limitMessage = `Maximum ${activeMaxPrnPerSearch} payment reference numbers allowed per search.`;
        setError(limitMessage);
        setSearchPerformed(true);

        Swal.fire({
          title: "Too Many PRNs",
          text: limitMessage,
          icon: "warning",
          confirmButtonColor: "#0ea5e9",
        });
        return;
      }

      // Align with app auth storage keys
      const token = localStorage.getItem("authentication/token");
      const tokenType = localStorage.getItem("authentication/token_type") || "Bearer";
      
      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const payload = {
        payment_ref_no_list: normalizedPrnList,
        provider_name: providerNameFilter.trim(),
      };

      const response = await fetch(`${BASE_URL}/provider-payment/dpr/search-prn-records`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `${tokenType} ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `Error: ${response.status} ${response.statusText}`
        );
      }

      if (data.status === "success") {
        setResults(data.data || []);
        setResultsSearchTerm("");
        setSortConfig({ key: "payment_ref_no", direction: "asc" });
        setSearchCriteria(data.search_criteria);
        setSearchPerformed(true);

        if (data.data && data.data.length > 0) {
          Swal.fire({
            title: "Success",
            text: `Found ${data.data.length} record(s) matching your search criteria.`,
            icon: "success",
            confirmButtonColor: "#0ea5e9",
          });
        } else {
          Swal.fire({
            title: "No Results",
            text: "No records found matching the provided PRN and filters.",
            icon: "info",
            confirmButtonColor: "#0ea5e9",
          });
        }
      } else {
        throw new Error(data.message || "Unknown error occurred");
      }
    } catch (err) {
      console.error("Search error:", err);
      const errorMsg = err.message || "An error occurred while searching for records.";
      setError(errorMsg);
      setSearchPerformed(true);

      Swal.fire({
        title: "Search Error",
        text: errorMsg,
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPrnInput("");
    setProviderNameFilter("");
    setResults([]);
    setResultsSearchTerm("");
    setSortConfig({ key: "payment_ref_no", direction: "asc" });
    setSearchPerformed(false);
    setError(null);
    setSearchCriteria(null);
  };

  const handleDownloadFile = (fileUrl, fileName) => {
    if (!fileUrl) {
      Swal.fire({
        title: "No File",
        text: "This file is not available.",
        icon: "info",
        confirmButtonColor: "#0ea5e9",
      });
      return;
    }

    // Open in new tab/window
    window.open(fileUrl, "_blank");
  };

  const renderFileActions = ({
    v1Url,
    v2Url,
    v1FileName,
    v2FileName,
    v1ClassName,
    v2ClassName,
    v1Title,
    v2Title,
  }) => {
    if (!v1Url && !v2Url) {
      return <span className="text-gray-400 text-xs">-</span>;
    }

    return (
      <div className="inline-flex flex-wrap items-center justify-center gap-1">
        {v1Url && (
          <button
            onClick={() => handleDownloadFile(v1Url, v1FileName)}
            className={v1ClassName}
            title={v1Title}
          >
            <FontAwesomeIcon icon={faEye} />
            View
          </button>
        )}
        {v2Url && (
          <button
            onClick={() => handleDownloadFile(v2Url, v2FileName)}
            className={v2ClassName}
            title={v2Title}
          >
            <FontAwesomeIcon icon={faEye} />
            View V2
          </button>
        )}
      </div>
    );
  };

  const buildExportRows = () => {
    return filteredAndSortedResults.map((record) => ({
      "Payment Ref No.": record.payment_ref_no || "",
      "Provider Name": record.provider_name || "",
      "PIS File URL (V1)": record.pis_file || "",
      "PIS File URL (V2)": record.pis_file_v2 || "",
      "Deposit Slip URL (V1)": record.deposit_slip_file || "",
      "Deposit Slip URL (V2)": record.deposit_slip_file_v2 || "",
      "Form 2307 URL (V1)": record.form_2307_file || "",
      "Form 2307 URL (V2)": record.form_2307_file_v2 || "",
    }));
  };

  const buildExportFileName = (ext) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `find_data_using_prn_results_${stamp}.${ext}`;
  };

  const escapeCsvValue = (value) => {
    const raw = value == null ? "" : String(value);
    const escaped = raw.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const handleExportCsv = () => {
    if (!filteredAndSortedResults.length) {
      Swal.fire({
        title: "No Data",
        text: "No results available to export.",
        icon: "info",
        confirmButtonColor: "#0ea5e9",
      });
      return;
    }

    const rows = buildExportRows();
    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => headers.map((h) => escapeCsvValue(row[h])).join(",")),
    ];

    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = buildExportFileName("csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  const handleExportXlsx = async () => {
    if (!filteredAndSortedResults.length) {
      Swal.fire({
        title: "No Data",
        text: "No results available to export.",
        icon: "info",
        confirmButtonColor: "#0ea5e9",
      });
      return;
    }

    const rows = buildExportRows();
    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PRN Results");
    XLSX.writeFile(workbook, buildExportFileName("xlsx"));
  };

  const handlePrnTextareaKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!loading) {
        handleSearch();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3 mb-2">
            <span className="text-2xl">🔍</span>
            Find Data Using PRN Number
          </h1>
          <p className="text-gray-600">
            Search for settlement records, provider information, and associated documents by Payment Reference Number (PRN).
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[30%_70%] gap-6 items-start">
          <div className="space-y-6">
            {/* Search Form Card */}
            <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
              <form onSubmit={handleSearch}>
                <div className="space-y-5 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Payment Reference Number(s) <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={prnInput}
                      onChange={(e) => setPrnInput(e.target.value)}
                      onKeyDown={handlePrnTextareaKeyDown}
                      placeholder="Enter PRN(s) separated by commas&#10;Example: P-000548-20250827-834881,P-000548-20250818-831785"
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-blue-500 focus:outline-none font-mono text-sm resize-y min-h-[120px]"
                      rows="4"
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      You can paste multiple PRNs separated by commas or new lines. Maximum {maxPrnPerSearch} PRNs per search.
                    </p>
                    {prnSearchLimitError && (
                      <p className="text-xs text-amber-700 mt-2">
                        {prnSearchLimitError}
                      </p>
                    )}
                    <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
                      <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">Tip</span>
                      Press Enter to Search. Hold Shift + Enter for a New Line.
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Provider Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={providerNameFilter}
                      onChange={(e) => setProviderNameFilter(e.target.value)}
                      placeholder="Enter provider name to filter results (exact prefix match)"
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Leave empty to see results from all providers.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 items-center">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all ${
                      loading
                        ? "bg-slate-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
                    }`}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSearch} />
                        Search
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={loading}
                    className="px-5 py-2.5 rounded-lg font-semibold border-2 border-slate-300 text-gray-700 hover:bg-slate-50 transition-all disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-3">PRN Format</h3>
                <p className="text-sm text-gray-700 mb-2">
                  Payment Reference Numbers typically follow this format:
                </p>
                <code className="block bg-slate-100 p-3 rounded text-xs font-mono text-gray-800 overflow-x-auto">
                  P-XXXXXX-YYYYMMDD-XXXXXX
                </code>
                <p className="text-xs text-gray-500 mt-2">
                  Example: P-000548-20250827-834881
                </p>
              </div>

              <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Tips</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>Use commas or new lines to separate multiple PRNs</li>
                  <li>Provider filter is optional (prefix match)</li>
                  <li>Maximum {maxPrnPerSearch} PRNs per search</li>
                  <li>Click View buttons to access document files</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 mt-1" />
                  <div>
                    <h3 className="font-semibold text-red-800">Search Error</h3>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Search Info */}
            {searchPerformed && searchCriteria && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900">Search Summary</h3>
                    <p className="text-blue-800 text-sm mt-1">
                      {results.length > 0
                        ? `Found ${results.length} record(s) `
                        : "No records found "}{" "}
                      with <strong>{searchCriteria.prn_count} PRN(s)</strong>
                      {searchCriteria.max_payment_ref_no_per_search && (
                        <>
                          {" "}
                          out of <strong>{searchCriteria.max_payment_ref_no_per_search}</strong> allowed
                        </>
                      )}
                      {searchCriteria.provider_filter !== "None" && (
                        <>
                          {" "}
                          and provider filter: <strong>{searchCriteria.provider_filter}</strong>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Results Table */}
            {searchPerformed && results.length > 0 && (
              <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-sm text-slate-700">Export current search results</p>
                    <input
                      type="text"
                      value={resultsSearchTerm}
                      onChange={(e) => setResultsSearchTerm(e.target.value)}
                      placeholder="Search all columns"
                      className="w-64 max-w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative">
                      <span className="absolute -top-2 -left-2 z-10 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white shadow">
                        New
                      </span>
                      <button
                        type="button"
                        onClick={handleExportCsv}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition-colors"
                      >
                        <FontAwesomeIcon icon={faFileCsv} />
                        Export CSV
                      </button>
                    </div>

                    <div className="relative">
                      <span className="absolute -top-2 -left-2 z-10 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white shadow">
                        New
                      </span>
                      <button
                        type="button"
                        onClick={handleExportXlsx}
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
                      >
                        <FontAwesomeIcon icon={faFileExcel} />
                        Export XLSX
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-300">
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          <button type="button" onClick={() => handleSort("payment_ref_no")} className="inline-flex items-center gap-1 hover:text-blue-600">
                            Payment Ref No. <span className="text-xs">{getSortIndicator("payment_ref_no")}</span>
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          <button type="button" onClick={() => handleSort("provider_name")} className="inline-flex items-center gap-1 hover:text-blue-600">
                            Provider Name <span className="text-xs">{getSortIndicator("provider_name")}</span>
                          </button>
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">
                          <button type="button" onClick={() => handleSort("pis_file")} className="inline-flex items-center gap-1 hover:text-blue-600">
                            PIS File <span className="text-xs">{getSortIndicator("pis_file")}</span>
                          </button>
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">
                          <button type="button" onClick={() => handleSort("deposit_slip_file")} className="inline-flex items-center gap-1 hover:text-blue-600">
                            Deposit Slip <span className="text-xs">{getSortIndicator("deposit_slip_file")}</span>
                          </button>
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">
                          <button type="button" onClick={() => handleSort("form_2307_file")} className="inline-flex items-center gap-1 hover:text-blue-600">
                            Form 2307 <span className="text-xs">{getSortIndicator("form_2307_file")}</span>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedResults.map((record, index) => (
                        <tr
                          key={index}
                          className={`border-b border-slate-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-slate-100 transition-colors`}
                        >
                          <td className="px-4 py-3 font-mono text-gray-900">
                            {record.payment_ref_no}
                          </td>
                          <td className="px-4 py-3 text-gray-800">
                            {record.provider_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {renderFileActions({
                              v1Url: record.pis_file,
                              v2Url: record.pis_file_v2,
                              v1FileName: `PIS_${record.payment_ref_no}`,
                              v2FileName: `PIS_V2_${record.payment_ref_no}`,
                              v1ClassName: "inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-xs font-semibold",
                              v2ClassName: "inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-semibold",
                              v1Title: "View PIS file (V1)",
                              v2Title: "View PIS file (V2)",
                            })}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {renderFileActions({
                              v1Url: record.deposit_slip_file,
                              v2Url: record.deposit_slip_file_v2,
                              v1FileName: `DepositSlip_${record.payment_ref_no}`,
                              v2FileName: `DepositSlip_V2_${record.payment_ref_no}`,
                              v1ClassName: "inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-xs font-semibold",
                              v2ClassName: "inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-xs font-semibold",
                              v1Title: "View deposit slip (V1)",
                              v2Title: "View deposit slip (V2)",
                            })}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {renderFileActions({
                              v1Url: record.form_2307_file,
                              v2Url: record.form_2307_file_v2,
                              v1FileName: `Form2307_${record.payment_ref_no}`,
                              v2FileName: `Form2307_V2_${record.payment_ref_no}`,
                              v1ClassName: "inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors text-xs font-semibold",
                              v2ClassName: "inline-flex items-center gap-1 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-xs font-semibold",
                              v1Title: "View form 2307 (V1)",
                              v2Title: "View form 2307 (V2)",
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 text-sm text-gray-600">
                  <span className="font-semibold">{filteredAndSortedResults.length}</span> of <span className="font-semibold">{results.length}</span> record(s) displayed
                </div>
              </div>
            )}

            {/* No Results Message */}
            {searchPerformed && results.length === 0 && !error && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded text-center">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-amber-600 text-3xl mb-3"
                />
                <h3 className="font-semibold text-amber-900 mb-1">No Results Found</h3>
                <p className="text-amber-800 text-sm">
                  No settlement records match the provided Payment Reference Numbers and filters.
                </p>
                <p className="text-amber-700 text-xs mt-3">
                  Tip: Check the PRN format and verify the provider name filter if used.
                </p>
              </div>
            )}

            {/* Initial State */}
            {!searchPerformed && (
              <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-12 text-center">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-slate-400 text-4xl mb-4"
                />
                <h3 className="font-semibold text-gray-700 mb-2">
                  Ready to Search
                </h3>
                <p className="text-gray-600 text-sm max-w-md mx-auto">
                  Enter one or more Payment Reference Numbers (PRN) above to search for settlement records,
                  provider information, and associated documents.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindDataUsingPrnPage;
